import React from 'react';
import { ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import LoteCardExpandido from './LoteCardExpandido';

export default function CategoriaCard({
  categoria,
  lotes,
  isExpandida,
  onToggleExpand,
  limiteOF,
  getCategoriaCor,
  // Props para ações de lotes
  ofId,
  aprovando,
  onAprovarLote,
  onEliminarLote,
  onAvancarLote,
  onDividirLote,
  onGerarPDF,
  onEditarQuantidade,
  onTerceirizar,
}) {
  // Calcular métricas da categoria
  const lotesCategoria = lotes.filter(l => l.categoria_id === categoria.id);
  const lotesPendentesCategoria = lotesCategoria.filter(l => l.status === 'pendente');
  const lotesAprovadosCategoria = lotesCategoria.filter(l => l.status === 'aprovado');
  const totalCategoria = lotesCategoria.reduce((acc, l) => acc + (l.quantidade || 0), 0);
  const produzidoCategoria = lotesCategoria.reduce((acc, l) => acc + (l.quantidade_produzida || 0), 0);
  const percentagem = limiteOF > 0 ? Math.round((totalCategoria / limiteOF) * 100) : 0;
  
  // Calcular totais de contagem e anomalias da categoria
  const temContagemCategoria = lotesCategoria.some(l => l.resumo_operacoes?.tem_contagem);
  const totalAnomaliasCategoria = lotesCategoria.reduce((acc, l) => acc + (l.resumo_operacoes?.total_anomalias || 0), 0);
  
  // Calcular quantidades originais e revistas da categoria
  const qtdOriginalCategoria = lotesCategoria.reduce((acc, l) => acc + (l.resumo_operacoes?.quantidade_original || l.quantidade || 0), 0);
  const qtdRevistaCategoria = lotesCategoria.reduce((acc, l) => acc + (l.resumo_operacoes?.quantidade_revista || l.quantidade_ajustada || l.quantidade || 0), 0);
  
  // Calcular resumo de contagem por lote
  const resumoContagem = lotesCategoria
    .filter(l => l.resumo_operacoes?.tem_contagem)
    .map(l => ({
      codigo: l.codigo_lote || `L${l.numero_lote}`,
      original: l.resumo_operacoes?.quantidade_original || l.quantidade,
      ajustada: l.resumo_operacoes?.quantidade_revista || l.quantidade_ajustada || l.quantidade,
      anomalias: l.resumo_operacoes?.total_anomalias || 0,
      contagemRealizada: l.resumo_operacoes?.contagem_realizada || false
    }));
  
  // Cores de estado baseadas em progresso
  let corProgresso = 'bg-stone-200';
  if (totalCategoria > 0) {
    const progressoPct = (produzidoCategoria / totalCategoria) * 100;
    if (progressoPct >= 100) corProgresso = 'bg-green-500';
    else if (progressoPct >= 50) corProgresso = 'bg-amber-500';
    else corProgresso = 'bg-blue-500';
  }
  
  return (
    <div 
      className={`rounded-xl border-2 overflow-hidden transition-all cursor-pointer hover:shadow-lg ${
        isExpandida ? 'ring-2 ring-navy' : ''
      } ${getCategoriaCor(categoria.codigo)}`}
      onClick={() => onToggleExpand(isExpandida ? null : categoria.id)}
    >
      {/* Cabeçalho da categoria */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold bg-white/70 px-2 py-0.5 rounded">
              {categoria.codigo || '---'}
            </span>
            <span className="text-sm font-medium truncate">{categoria.nome}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-stone-500">{lotesCategoria.length} lotes</span>
            {isExpandida ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        </div>
        
        {/* Métricas principais */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold">{totalCategoria}</p>
            <p className="text-xs opacity-70">
              {percentagem}% do limite ({limiteOF})
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold">{produzidoCategoria}/{totalCategoria}</p>
            <p className="text-xs opacity-70">produzidas</p>
          </div>
        </div>
        
        {/* Barra de progresso */}
        <div className="mt-2 h-2 bg-white/50 rounded-full overflow-hidden">
          <div 
            className={`h-full ${corProgresso} transition-all`}
            style={{ width: `${totalCategoria > 0 ? (produzidoCategoria / totalCategoria) * 100 : 0}%` }}
          />
        </div>
        
        {/* Badges de estado */}
        <div className="flex gap-2 mt-2 items-center">
          {lotesPendentesCategoria.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">
              {lotesPendentesCategoria.length} pendente{lotesPendentesCategoria.length > 1 ? 's' : ''}
            </span>
          )}
          {lotesAprovadosCategoria.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-300">
              {lotesAprovadosCategoria.length} aprov.
            </span>
          )}
          {/* Indicador visual para expandir lotes */}
          {lotesCategoria.length > 0 && !isExpandida && (
            <span className="ml-auto text-[10px] text-stone-400 flex items-center gap-1 animate-pulse">
              clique para ver lotes
              <ChevronDown className="h-3 w-3" />
            </span>
          )}
        </div>
        
        {/* Resumo de Quantidades da Categoria - Mostra se tem contagem OU anomalias */}
        {(temContagemCategoria || totalAnomaliasCategoria > 0) && (
          <div 
            className="mt-3 space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Métricas Principais - Layout simples sem molduras */}
            <div className={`flex ${temContagemCategoria ? 'justify-between' : 'justify-end'} items-end gap-4`}>
              {temContagemCategoria && (
                <>
                  <div className="text-center">
                    <p className="text-[10px] uppercase tracking-wide text-blue-600 font-semibold">Entrada</p>
                    <p className="text-2xl font-bold text-blue-600">{qtdOriginalCategoria}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-green-600">Revista</p>
                    <p className="text-2xl font-bold text-green-600">
                      {qtdRevistaCategoria}
                    </p>
                  </div>
                </>
              )}
              <div className="text-center">
                <p className={`text-[10px] uppercase tracking-wide font-semibold ${totalAnomaliasCategoria > 0 ? 'text-red-600' : 'text-stone-400'}`}>Anomalias</p>
                <p className={`text-2xl font-bold ${totalAnomaliasCategoria > 0 ? 'text-red-600' : 'text-stone-300'}`}>
                  {totalAnomaliasCategoria}
                </p>
              </div>
            </div>
            
            {/* Detalhe por Lote - Colapsável (só se tem contagem) */}
            {temContagemCategoria && resumoContagem.length > 0 && (
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-[10px] uppercase tracking-wide text-slate-500 font-medium hover:text-slate-700 transition-colors list-none">
                  <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                  Por Lote ({resumoContagem.length})
                </summary>
                <div className="mt-1.5 space-y-1 pl-5">
                  {resumoContagem.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-white/80 rounded px-2 py-1.5 border border-slate-100">
                      <span className="font-mono font-semibold text-slate-700">{r.codigo}</span>
                      <div className="flex items-center gap-2">
                        {r.original !== r.ajustada ? (
                          <span className="text-amber-600 font-medium">
                            {r.original} → {r.ajustada}
                          </span>
                        ) : (
                          <span className="text-slate-600">{r.original} pcs</span>
                        )}
                        {r.anomalias > 0 && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px]">
                            {r.anomalias} an.
                          </span>
                        )}
                        {!r.contagemRealizada && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded text-[10px]">
                            pendente
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
      
      {/* Detalhes expandidos - Lista de lotes */}
      {isExpandida && lotesCategoria.length > 0 && (
        <div className="border-t bg-white/80 p-3 space-y-2">
          <p className="text-xs font-semibold text-stone-600 mb-2">Lotes nesta categoria:</p>
          {lotesCategoria.map(lote => (
            <LoteCardExpandido
              key={lote.id}
              lote={lote}
              ofId={ofId}
              aprovando={aprovando}
              onAprovar={onAprovarLote}
              onEliminar={onEliminarLote}
              onAvancar={onAvancarLote}
              onDividir={onDividirLote}
              onGerarPDF={onGerarPDF}
              onEditarQuantidade={onEditarQuantidade}
              onTerceirizar={onTerceirizar}
            />
          ))}
        </div>
      )}
      
      {/* Sem lotes */}
      {isExpandida && lotesCategoria.length === 0 && (
        <div className="border-t bg-white/80 p-3 text-center text-sm text-stone-500">
          Sem lotes nesta categoria
        </div>
      )}
    </div>
  );
}
